import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Recycle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getPowerRecycle, planPowerRecycle } from "@/lib/api";

export const PowerRecycleCard = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback((force) => getPowerRecycle(force).then(setD).catch(() => {}), []);

  useEffect(() => { load(false); }, [load]);

  const refresh = async () => {
    setBusy(true);
    await load(true);
    setBusy(false);
  };

  const prepare = async (sig) => {
    try {
      const p = await planPowerRecycle(sig);
      toast.success(`Preparata come ipotesi unica su ${p.symbols.length} mercati (${p.pooledN} trade)`);
    } catch { toast.error("Preparazione non riuscita"); }
  };

  if (!d) return null;
  return (
    <Card className="border-border/70 bg-card/60" data-testid="power-recycle-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <Recycle className="h-4 w-4 text-emerald-300" /> Riciclo per potenza · rinviate, non falsificate
          <Badge variant="outline" className="text-[10px] border-border text-muted-foreground"
            data-testid="power-recycle-count">
            {d.recuperabili} famiglie recuperabili su {d.famiglie}
          </Badge>
          <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto" onClick={refresh}
            disabled={busy} data-testid="power-recycle-refresh">
            <RefreshCw className={cn("h-3 w-3", busy && "animate-spin")} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="power-recycle-kpis">
          {[
            ["Rinviate per pochi trade", d.rinviate],
            ["Trade recuperabili", d.tradeRecuperati],
            ["Prove risparmiate", d.provePotenzialmenteRisparmiate],
            ["Senza spec", d.senzaSpec],
          ].map(([k, v]) => (
            <div key={k} className="rounded-md border border-border/60 p-2 text-center">
              <div className="font-mono text-base text-white/85">{v}</div>
              <div className="text-[10px] text-muted-foreground">{k}</div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">{d.regola}</p>

        <div className="space-y-1" data-testid="power-recycle-concepts">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Dove sono finiti i tentativi · {d.concetti} concetti in {d.famiglie} famiglie
          </p>
          {(d.topConcetti || []).slice(0, 5).map((c) => (
            <div key={c.concept} className="flex flex-wrap items-baseline gap-2 font-mono text-[11px]"
              data-testid={`power-recycle-concept-${c.concept}`}>
              <span className="text-amber-300/90">{c.trials} tentativi</span>
              <span className="text-white/35">{c.famiglie} soglie diverse</span>
              <span className="text-white/60 truncate max-w-[46ch]">{c.rule}</span>
              <span className="text-white/30">{c.pooledN} trade</span>
            </div>
          ))}
          <p className="text-[10px] text-amber-300/70 leading-relaxed">
            {d.sogliaAvvertenza}
          </p>
        </div>

        <div className="space-y-1.5">
          {(d.top || []).slice(0, 10).map((g) => (
            <div key={g.sig}
              className={cn("rounded border px-2 py-1.5 space-y-0.5",
                g.recuperabile ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-border/50")}
              data-testid={`power-recycle-row-${g.sig}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-[11px] text-white/80">{g.rule}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {g.nTrials} tentativi · {g.pooledN} trade in comune
                </span>
                {g.tassa && (
                  <span className="font-mono text-[10px] text-amber-300/90">
                    tassa {g.tassa.oggi} → {g.tassa.unita}
                  </span>
                )}
                {g.recuperabile && (
                  <Button size="sm" variant="outline" className="h-6 ml-auto text-[10px]"
                    onClick={() => prepare(g.sig)}
                    data-testid={`power-recycle-plan-${g.sig}`}>
                    Prepara come ipotesi unica
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground/80">
                {g.verdetto} · {(g.symbols || []).join(", ")}
              </p>
            </div>
          ))}
          {!(d.top || []).length && (
            <p className="text-[11px] text-muted-foreground" data-testid="power-recycle-empty">
              Nessuna ipotesi rinviata da riciclare.
            </p>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground/70">{d.come}</p>
      </CardContent>
    </Card>
  );
};

export default PowerRecycleCard;
